'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenAmountPrice } from '@morpho-org/ui-v6/client'
import { Skeleton } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { rowSortingFeature, tableFeatures } from '@tanstack/react-table'

import { TimestampCell } from '@/components/common/Cells/TimestampCell'
import { TransactionHashCell } from '@/components/common/Cells/TransactionHashCell'
import { UserCellDashboard, UserCellExplorer } from '@/components/common/Cells/UserCell'

import type { LiquidationTableDataRow } from './LiquidationTable.types'

// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
export const liquidationTableFeatures = tableFeatures({
  rowSortingFeature
})
export type LiquidationTableFeatures = typeof liquidationTableFeatures

export const columnsLiquidationTable: ColumnDefWithSkeleton<
  LiquidationTableFeatures,
  LiquidationTableDataRow
>[] = [
  {
    id: 'date',
    accessorKey: 'date',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Date & Time</span>
        </TooltipTrigger>
        <TooltipContent>The date and time the liquidation was executed.</TooltipContent>
      </Tooltip>
    ),
    cell: info => <TimestampCell timestamp={info.row.original.date.timestamp} />,
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[122px] rounded-[20px]" />
  },
  {
    id: 'liquidated',
    accessorKey: 'liquidated',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Liquidated Wallet
          </span>
        </TooltipTrigger>
        <TooltipContent>The address of the liquidated borrower.</TooltipContent>
      </Tooltip>
    ),
    cell: info => <UserCellDashboard address={info.row.original.liquidated.address} />,
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[140px] rounded-[20px]" />
  },
  {
    id: 'loanRepaid',
    accessorKey: 'loanRepaid',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Loan Repaid</span>
        </TooltipTrigger>
        <TooltipContent>The amount of debt that was repaid during liquidation.</TooltipContent>
      </Tooltip>
    ),
    cell: info => (
      <CellTokenAmountPrice
        token={info.row.original.loanAsset}
        amount={info.row.original.loanRepaid.amount}
        withAvatar
        value={info.row.original.loanRepaid.amountUsd}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[119px] rounded-[20px]" />
  },
  {
    id: 'collateralSeized',
    accessorKey: 'collateralSeized',
    header: info => {
      const table = info.table
      const firstRow = table.getRowModel().rows[0]
      const collateralAsset = firstRow?.original.collateralAsset

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground">
              Collateral Seized
              {collateralAsset ? ` (${collateralAsset.symbol})` : ''}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            The amount of collateral seized from the liquidated position.
          </TooltipContent>
        </Tooltip>
      )
    },
    cell: info => (
      <CellTokenAmountPrice
        token={info.row.original.collateralAsset}
        amount={info.row.original.collateralSeized.amount}
        value={info.row.original.collateralSeized.amountUsd}
        withAvatar
        omitSymbol
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[119px] rounded-[20px]" />
  },
  {
    id: 'realizedBadDebt',
    accessorKey: 'realizedBadDebt',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Realized Bad Debt</span>
        </TooltipTrigger>
        <TooltipContent>
          The amount of debt that could not be recovered through liquidation.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenAmountPrice
        token={row.original.loanAsset}
        amount={row.original.realizedBadDebt.amount}
        value={row.original.realizedBadDebt.amountUsd}
        withAvatar
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[119px] rounded-[20px]" />
  },
  {
    id: 'liquidator',
    accessorKey: 'liquidator',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Liquidator
          </span>
        </TooltipTrigger>
        <TooltipContent>The address of the liquidator.</TooltipContent>
      </Tooltip>
    ),
    cell: info => (
      <UserCellExplorer
        address={info.row.original.liquidator.address}
        chainId={info.row.original.chainId}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[140px] rounded-[20px]" />
  },
  {
    id: 'tx',
    accessorKey: 'tx',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '7px' }}>
            Transaction
          </span>
        </TooltipTrigger>
        <TooltipContent>The unique identifier of the transaction.</TooltipContent>
      </Tooltip>
    ),
    cell: info => (
      <TransactionHashCell hash={info.row.original.tx.hash} chainId={info.row.original.chainId} />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[119px] rounded-[20px]" />
  }
]

// Same order as the columns above, so the column menu reads like the table.
export const columnLabelsLiquidationTable = {
  date: 'Date & Time',
  liquidated: 'Liquidated Wallet',
  loanRepaid: 'Loan Repaid',
  collateralSeized: 'Collateral Seized',
  realizedBadDebt: 'Realized Bad Debt',
  liquidator: 'Liquidator',
  tx: 'Transaction'
}
